/**
 * MARS forecasts / expected-returns API client.
 *
 * Thin wrappers around the backend endpoints the feature consumes. The regular
 * `{success, data, error}` endpoints go through the shared `apiGetUnwrapped`
 * helper, which unwraps the envelope and throws on `success: false` so
 * TanStack Query can surface the error to the UI. The decomposition
 * dates/config and returns-vintage endpoints are irregular (payload at the
 * TOP level, not in `data`) and keep bespoke fetchers below.
 *
 * Endpoints (see `backend/pages/mars/api.py` and
 * `backend/pages/economic_outlook/api.py` for inflation/recession):
 *   - GET /api/mars/forecasts/returns?view=<term-structure|nominal-real|expected>
 *   - GET /api/economic-outlook/inflation?country=<>&type=<Core|Headline>&timeframe=<>
 *   - GET /api/economic-outlook/recession?country=<>&timeframe=<>
 *   - GET /api/mars/forecasts/svar
 *   - GET /api/mars/forecasts/decomposition (superuser)
 *   - GET /api/mars/forecasts/decomposition/dates?mandate=<>
 *   - GET /api/mars/forecasts/decomposition/config
 */

import { apiGet } from '@/lib/api/client';
import { apiGetUnwrapped, type ApiEnvelope } from '@/lib/api/envelope';
import { endpoints } from '@/lib/api/endpoints';
import type {
  CountryCode,
  DecompositionAsset,
  DecompositionConfig,
  DecompositionDate,
  DecompositionMandate,
  DecompositionPayload,
  ExpectedReturnsRow,
  InflationRow,
  InflationType,
  NominalRealRow,
  RecessionRow,
  SvarData,
  TermStructureRow,
  Timeframe,
} from './types';

/** Default long-history window for the expected-returns timeseries chart. */
const EXPECTED_RETURNS_DEFAULT_TIMEFRAME = '70y';

/** /api/mars/forecasts/returns?view=term-structure — term-structure lines per asset / vintage. */
export function fetchTermStructure(timeframe: Timeframe = '2y'): Promise<TermStructureRow[]> {
  return apiGetUnwrapped<TermStructureRow[]>(
    endpoints.mars.returns({ view: 'term-structure', timeframe }),
  );
}

/** /api/mars/forecasts/returns?view=nominal-real — nominal / real bar data by horizon. */
export function fetchNominalReal(timeframe: Timeframe = '2y'): Promise<NominalRealRow[]> {
  return apiGetUnwrapped<NominalRealRow[]>(
    endpoints.mars.returns({ view: 'nominal-real', timeframe }),
  );
}

/** /api/mars/forecasts/returns?view=expected — long-history timeseries (70y default). */
export function fetchExpectedReturns(
  timeframe: string = EXPECTED_RETURNS_DEFAULT_TIMEFRAME,
): Promise<ExpectedReturnsRow[]> {
  return apiGetUnwrapped<ExpectedReturnsRow[]>(
    endpoints.mars.returns({ view: 'expected', timeframe }),
  );
}

/** /api/economic-outlook/inflation — inflation fan chart. */
export function fetchInflationData(
  country: CountryCode,
  inflationType: InflationType,
  timeframe: Timeframe = '5y',
): Promise<InflationRow[]> {
  return apiGetUnwrapped<InflationRow[]>(
    endpoints.forecasts.inflation({ country, type: inflationType, timeframe }),
  );
}

/** /api/economic-outlook/recession — recession / strong expansion probabilities. */
export function fetchRecessionProbabilities(
  country: CountryCode,
  timeframe: Timeframe = '2y',
): Promise<RecessionRow[]> {
  return apiGetUnwrapped<RecessionRow[]>(endpoints.forecasts.recession({ country, timeframe }));
}

/** /api/mars/forecasts/svar — high-frequency SVAR shock decompositions (all variables). */
export function fetchSvar(): Promise<SvarData> {
  return apiGetUnwrapped<SvarData>(endpoints.mars.svar());
}

/**
 * /api/mars/forecasts/decomposition — term-structure decomposition (superuser-only).
 *
 * `old_date` / `new_date` accept YYYYMMDD strings. Leaving them undefined uses
 * backend defaults (latest / ~1 month prior).
 */
export function fetchDecomposition(params: {
  mandate: DecompositionMandate;
  asset: DecompositionAsset;
  oldDate?: string;
  newDate?: string;
}): Promise<DecompositionPayload> {
  return apiGetUnwrapped<DecompositionPayload>(
    endpoints.mars.decomposition({
      mandate: params.mandate,
      asset: params.asset,
      old_date: params.oldDate,
      new_date: params.newDate,
    }),
  );
}

/** /api/mars/forecasts/decomposition/dates — available snapshot dates per mandate. */
export function fetchDecompositionDates(
  mandate: DecompositionMandate,
  limit = 90,
): Promise<DecompositionDate[]> {
  // This endpoint returns {dates: [...]} at the top level, not inside `data`.
  return apiGet<ApiEnvelope<DecompositionDate[]> & { dates: DecompositionDate[] }>(
    endpoints.mars.decompositionDates({ mandate, limit }),
  ).then((envelope) => {
    if (!envelope.success) {
      throw new Error(envelope.error ?? 'Failed to fetch decomposition dates');
    }
    return envelope.dates;
  });
}

/** /api/mars/forecasts/decomposition/config — mandate/asset options metadata. */
export function fetchDecompositionConfig(): Promise<DecompositionConfig> {
  // Backend returns `mandates` / `assets` at the top level, not inside `data`.
  return apiGet<ApiEnvelope<unknown> & DecompositionConfig>(
    endpoints.mars.decompositionConfig(),
  ).then((envelope) => {
    if (!envelope.success) {
      throw new Error(envelope.error ?? 'Failed to fetch decomposition config');
    }
    return { mandates: envelope.mandates, assets: envelope.assets };
  });
}

/** Stable TanStack Query keys for this feature. */
export const marsForecastsKeys = {
  all: ['mars-forecasts'] as const,
  termStructure: (tf: Timeframe) => ['mars-forecasts', 'term-structure', tf] as const,
  nominalReal: (tf: Timeframe) => ['mars-forecasts', 'nominal-real', tf] as const,
  expectedReturns: (tf: string) => ['mars-forecasts', 'expected-returns', tf] as const,
  inflation: (country: CountryCode, type: InflationType, tf: Timeframe) =>
    ['mars-forecasts', 'inflation', country, type, tf] as const,
  recession: (country: CountryCode, tf: Timeframe) =>
    ['mars-forecasts', 'recession', country, tf] as const,
  svar: () => ['mars-forecasts', 'svar'] as const,
  decomposition: (
    mandate: DecompositionMandate,
    asset: DecompositionAsset,
    old?: string,
    nw?: string,
  ) =>
    ['mars-forecasts', 'decomposition', mandate, asset, old ?? 'default', nw ?? 'latest'] as const,
  decompositionDates: (mandate: DecompositionMandate) =>
    ['mars-forecasts', 'decomposition-dates', mandate] as const,
  decompositionConfig: () => ['mars-forecasts', 'decomposition-config'] as const,
  returnsVintageList: () => ['mars-forecasts', 'returns-vintage-list'] as const,
  returnsVintageSnapshot: (date: string) =>
    ['mars-forecasts', 'returns-vintage-snapshot', date] as const,
};

// Vintage returns endpoints (DB-backed, slider-driven)

/** Wire shape of /api/mars/forecasts/returns/vintages/<date>/snapshot. */
export interface VintageReturnsSnapshot {
  [assetKey: string]:
    | {
        excess?: Array<{ horizon_months: number; value: number }>;
        nominal?: Array<{ horizon_months: number; value: number }>;
        real?: Array<{ horizon_months: number; value: number }>;
      }
    | Array<{ horizon_months: number; value: number }>;
}

/** /api/mars/forecasts/returns/vintages -- sorted vintage dates for returns slider. */
export async function fetchReturnsVintageList(): Promise<string[]> {
  const envelope = await apiGet<{ success: boolean; vintages: string[]; error?: string }>(
    endpoints.mars.returnsVintageList(),
  );
  if (!envelope.success) throw new Error(envelope.error ?? 'Failed to fetch returns vintage list');
  return envelope.vintages;
}

/** /api/mars/forecasts/returns/vintages/<date>/snapshot -- all assets+horizons for one vintage. */
export async function fetchReturnsVintageSnapshot(
  vintageDate: string,
  returnType: 'all' | 'excess' | 'nominal' | 'real' = 'all',
): Promise<VintageReturnsSnapshot> {
  const envelope = await apiGet<{
    success: boolean;
    data: VintageReturnsSnapshot;
    error?: string;
  }>(endpoints.mars.returnsVintageSnapshot(vintageDate, { return_type: returnType }));
  if (!envelope.success) throw new Error(envelope.error ?? 'Failed to fetch vintage snapshot');
  return envelope.data;
}
